import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Table } from 'react-bootstrap';

import BetaDistributorFilter from 'components/upb_viewer_beta/BetaDistributorFilter';
import BetaPdpSection from 'components/upb_viewer_beta/pdp/BetaPdpSection';
import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_TITLE,
  PRIMARY_COLOR,
  RADIUS_LG,
  RADIUS_MD,
  SHADOW_XS,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';
import { DASH, fmtInt, fmtMoney, fmtPct, fmtPrice } from 'components/upb_viewer_beta/pdp/format';
import useGet from 'lib/hooks/useGet';
import { UPB_VIEWER_BETA_SEGMENT_ANALYSIS } from 'lib/networking/endpoints';

function BetaSegmentAnalysisTab({ product }) {
  const [vendorId, setVendorId] = useState(null);

  const productId = product?.id;
  const { data, loading } = useGet(
    UPB_VIEWER_BETA_SEGMENT_ANALYSIS,
    { product_id: productId, vendor_id: vendorId || undefined },
    !productId,
  );

  const payload = data?.data;
  const summary = payload?.summary || {};
  const segments = payload?.segments || [];
  const cuisines = payload?.cuisines || [];
  const regions = payload?.regions || [];
  const topStates = payload?.top_states || [];
  const coverage = payload?.coverage || {};

  return (
    <div className={css(styles.tab)}>
      <SummaryStatsGrid summary={summary} loading={loading} />

      <div className={css(styles.filterRow)}>
        <span className={css(styles.filterLabel)}>Filter</span>
        <BetaDistributorFilter
          value={vendorId}
          onChange={selection => setVendorId(selection.vendorId)}
          compact
          showGroupFilters={false}
        />
      </div>

      <BetaPdpSection title="GMV by Customer Segment">
        {loading ? (
          <SkeletonBlock height={240} marginTop={8} />
        ) : segments.length === 0 ? (
          <EmptyState message="No customer-segment data for this product yet." />
        ) : (
          <>
            <CoverageCaption
              gmvPct={coverage.segments_gmv_pct}
              custPct={coverage.segments_customer_pct}
              dim="segment"
            />
            <SegmentStackBar segments={segments} />
            <SegmentTable segments={segments} />
          </>
        )}
      </BetaPdpSection>

      <BetaPdpSection title="GMV by Region">
        {loading ? (
          <SkeletonBlock height={120} marginTop={8} />
        ) : regions.length === 0 ? (
          <EmptyState message="No geographic data for this product yet." />
        ) : (
          <RegionGrid regions={regions} />
        )}
      </BetaPdpSection>

      <BetaPdpSection title="Top Cuisine Types">
        {loading ? (
          <SkeletonBlock height={200} marginTop={8} />
        ) : cuisines.length === 0 ? (
          <EmptyState message="No cuisine data for this product yet." />
        ) : (
          <>
            <CoverageCaption
              gmvPct={coverage.cuisines_gmv_pct}
              custPct={coverage.cuisines_customer_pct}
              dim="cuisine"
            />
            <BreakoutTable rows={cuisines} labelCol="Cuisine" withPct />
          </>
        )}
      </BetaPdpSection>

      <BetaPdpSection title="Top States">
        {loading ? (
          <SkeletonBlock height={160} marginTop={8} />
        ) : topStates.length === 0 ? (
          <EmptyState message="No state-level orders for this product yet." />
        ) : (
          <TopStatesTable states={topStates} />
        )}
      </BetaPdpSection>
    </div>
  );
}

BetaSegmentAnalysisTab.propTypes = {
  product: PropTypes.shape({ id: PropTypes.number }),
};

BetaSegmentAnalysisTab.defaultProps = {
  product: null,
};

export default BetaSegmentAnalysisTab;

function CoverageCaption({ gmvPct, custPct, dim }) {
  // Per-row percentages are share of the tagged subset, not of product GMV;
  // this caption makes that explicit. Hidden at full coverage.
  if (gmvPct == null && custPct == null) return null;
  const showGmv = gmvPct != null && gmvPct < 99.95;
  const showCust = custPct != null && custPct < 99.95;
  if (!showGmv && !showCust) return null;
  const parts = [];
  if (showGmv) parts.push(`${gmvPct.toFixed(1)}% of orders`);
  if (showCust) parts.push(`${custPct.toFixed(1)}% of customers`);
  return (
    <div className={css(coverageStyles.caption)}>
      <strong>Coverage:</strong> only {parts.join(' · ')} are tagged with a {dim}. Percentages below
      show share within the tagged subset, not of the product overall.
    </div>
  );
}

CoverageCaption.propTypes = {
  gmvPct: PropTypes.number,
  custPct: PropTypes.number,
  dim: PropTypes.string.isRequired,
};

CoverageCaption.defaultProps = { gmvPct: null, custPct: null };

function SegmentStackBar({ segments }) {
  // Slices <5% hide their inline label so it doesn't squeeze and wrap.
  return (
    <div className={css(stackStyles.bar)}>
      {segments.map(s => (
        <div
          key={s.id || s.name}
          className={css(stackStyles.slice)}
          style={{
            flex: s.pct_of_total > 0 ? s.pct_of_total : 0.01,
            backgroundColor: s.color || PRIMARY_COLOR,
          }}
          title={`${s.name} — ${s.pct_of_total}%`}
        >
          {s.pct_of_total >= 5 ? `${Math.round(s.pct_of_total)}%` : ''}
        </div>
      ))}
    </div>
  );
}

SegmentStackBar.propTypes = { segments: PropTypes.array.isRequired };

function SegmentTable({ segments }) {
  return (
    <div className={css(tableStyles.wrap)}>
      <Table borderless className={css(tableStyles.table)}>
        <thead>
          <tr>
            <th className={css(tableStyles.th)}>Segment</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Annualized GMV</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>% of total</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Customers</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Units</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Avg Selling Price</th>
          </tr>
        </thead>
        <tbody>
          {segments.map(s => (
            <tr key={s.id || s.name}>
              <td className={css(tableStyles.td, tableStyles.strong)}>
                <span
                  className={css(tableStyles.dot)}
                  style={{ backgroundColor: s.color || PRIMARY_COLOR }}
                />
                {s.name}
              </td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtMoney(s.gmv)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtPct(s.pct_of_total)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtInt(s.customers)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtInt(s.units)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtPrice(s.asp)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

SegmentTable.propTypes = { segments: PropTypes.array.isRequired };

function BreakoutTable({ rows, labelCol, withPct }) {
  return (
    <div className={css(tableStyles.wrap)}>
      <Table borderless className={css(tableStyles.table)}>
        <thead>
          <tr>
            <th className={css(tableStyles.th)}>{labelCol}</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Customers</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Annualized Units</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Annualized GMV</th>
            {withPct && <th className={css(tableStyles.th, tableStyles.numCol)}>% of total</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id || r.name}>
              <td className={css(tableStyles.td, tableStyles.strong)}>{r.name}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtInt(r.customers)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtInt(r.units)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtMoney(r.gmv)}</td>
              {withPct && (
                <td className={css(tableStyles.td, tableStyles.numCol)}>
                  {fmtPct(r.pct_of_total)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

BreakoutTable.propTypes = {
  rows: PropTypes.array.isRequired,
  labelCol: PropTypes.string.isRequired,
  withPct: PropTypes.bool,
};

BreakoutTable.defaultProps = { withPct: false };

function SummaryStatsGrid({ summary, loading }) {
  const cells = [
    { l: 'Top segment', v: summary.top_segment || DASH },
    { l: 'Fastest growing', v: summary.fastest_growing_segment || DASH },
    { l: 'Top region', v: summary.top_region || DASH },
    {
      l: 'Customers reached',
      v: loading ? DASH : fmtInt(summary.customers_reached),
    },
  ];
  return (
    <div className={css(styles.miniGrid)}>
      {cells.map((c, i) => (
        <div key={i} className={css(styles.statCard)}>
          <div className={css(styles.statCardLabel)}>{c.l}</div>
          <div className={css(styles.statCardValue)}>{c.v}</div>
        </div>
      ))}
    </div>
  );
}

SummaryStatsGrid.propTypes = {
  summary: PropTypes.object,
  loading: PropTypes.bool,
};

SummaryStatsGrid.defaultProps = { summary: {}, loading: false };

function RegionGrid({ regions }) {
  // Top region's bar at 100%; the rest scaled relative.
  const topGmv = regions.length > 0 ? regions[0].gmv || 0 : 0;
  return (
    <div className={css(regionStyles.grid)}>
      {regions.map(r => {
        const pctOfTop = topGmv > 0 ? Math.min(100, (r.gmv / topGmv) * 100) : 0;
        return (
          <div key={r.name} className={css(regionStyles.card)}>
            <div className={css(regionStyles.name)}>{r.name}</div>
            <div className={css(regionStyles.bar)}>
              <div className={css(regionStyles.fill)} style={{ width: `${pctOfTop}%` }} />
            </div>
            <div className={css(regionStyles.meta)}>
              <span>{fmtMoney(r.gmv)}</span>
              <span className={css(regionStyles.metaMuted)}>{fmtPct(r.pct_of_total)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

RegionGrid.propTypes = { regions: PropTypes.array.isRequired };

function TopStatesTable({ states }) {
  return (
    <div className={css(tableStyles.wrap)}>
      <Table borderless className={css(tableStyles.table)}>
        <thead>
          <tr>
            <th className={css(tableStyles.th)}>State</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Customers</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Annualized Units</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>Annualized GMV</th>
            <th className={css(tableStyles.th, tableStyles.numCol)}>% of total</th>
          </tr>
        </thead>
        <tbody>
          {states.map(s => (
            <tr key={s.state}>
              <td className={css(tableStyles.td, tableStyles.strong)}>{s.state}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtInt(s.customers)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtInt(s.units)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtMoney(s.gmv)}</td>
              <td className={css(tableStyles.td, tableStyles.numCol)}>{fmtPct(s.pct_of_total)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

TopStatesTable.propTypes = { states: PropTypes.array.isRequired };

function EmptyState({ message }) {
  return <div className={css(styles.emptyState)}>{message}</div>;
}

EmptyState.propTypes = { message: PropTypes.string };
EmptyState.defaultProps = { message: 'No data available for this product.' };

function SkeletonBlock({ height, marginTop }) {
  return <div className={css(styles.skeleton)} style={{ height, marginTop: marginTop || 0 }} />;
}

SkeletonBlock.propTypes = {
  height: PropTypes.number.isRequired,
  marginTop: PropTypes.number,
};

SkeletonBlock.defaultProps = { marginTop: 0 };

const styles = StyleSheet.create({
  tab: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  miniGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14,
  },
  statCard: {
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: '16px 18px',
    minHeight: 80,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  statCardLabel: {
    fontFamily: FONT_BODY,
    fontSize: 11,
    fontWeight: 600,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  statCardValue: {
    fontFamily: FONT_TITLE,
    // Match the page-header summary tiles.
    fontSize: 16,
    fontWeight: 700,
    color: FG_1,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
    fontVariantNumeric: 'tabular-nums',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
  },
  filterLabel: {
    fontFamily: FONT_BODY,
    fontSize: 11,
    fontWeight: 700,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  emptyState: {
    padding: '24px 0',
    color: FG_3,
    fontFamily: FONT_BODY,
    fontSize: 14,
    textAlign: 'center',
  },
  skeleton: {
    backgroundColor: BG_2,
    borderRadius: RADIUS_MD,
    width: '100%',
  },
});

const tableStyles = StyleSheet.create({
  wrap: {
    marginTop: 8,
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontFamily: FONT_BODY,
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    backgroundColor: BG_2,
    borderBottom: `1px solid ${BORDER_2}`,
    color: FG_3,
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  td: {
    padding: '12px 14px',
    borderBottom: `1px solid ${BORDER_2}`,
    color: FG_2,
    fontVariantNumeric: 'tabular-nums',
  },
  numCol: {
    textAlign: 'right',
  },
  strong: {
    fontWeight: 700,
    color: FG_1,
  },
  dot: {
    // verticalAlign nudge centers the dot against bold 13px label text.
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 8,
    verticalAlign: '-1px',
  },
});

const coverageStyles = StyleSheet.create({
  caption: {
    fontFamily: FONT_BODY,
    fontSize: 12,
    color: FG_3,
    lineHeight: 1.4,
    padding: '8px 12px',
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_MD,
    marginTop: 8,
    marginBottom: 10,
  },
});

const stackStyles = StyleSheet.create({
  // BG_2 underlay fills any rounding gap when slice percentages sum to <100.
  bar: {
    display: 'flex',
    height: 36,
    borderRadius: 8,
    overflow: 'hidden',
    border: `1px solid ${BORDER_2}`,
    backgroundColor: BG_2,
    marginTop: 8,
    marginBottom: 14,
  },
  slice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: WHITE,
    fontFamily: FONT_BODY,
    fontSize: 11,
    fontWeight: 700,
    textShadow: '0 1px 2px rgba(0,0,0,0.15)',
    minWidth: 0,
    transition: 'flex 0.2s',
  },
});

const regionStyles = StyleSheet.create({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    marginTop: 8,
  },
  card: {
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_MD,
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  name: {
    fontFamily: FONT_BODY,
    fontWeight: 700,
    fontSize: 13,
    color: FG_1,
  },
  bar: {
    height: 8,
    backgroundColor: BG_1,
    borderRadius: 9999,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    backgroundColor: PRIMARY_COLOR,
    borderRadius: 9999,
    transition: 'width 0.3s',
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontFamily: FONT_BODY,
    fontSize: 12,
    fontWeight: 600,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
  },
  metaMuted: {
    color: FG_3,
    fontWeight: 500,
  },
});
